// Merken-index: client-side filter op merknaam + actieve letter in de sprongbalk.
// Progressive enhancement: zonder JS blijft de volledige A-Z index staan, alleen
// het zoekveld is dan verborgen (het `hidden` attribuut gaat er hier af).
class MerkenIndex extends HTMLElement {
  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('[data-merken-group]'));
    this.tiles = Array.from(this.querySelectorAll('[data-merken-name]'));
    if (!this.tiles.length) return;

    this.links = Array.from(this.querySelectorAll('[data-merken-jump]'));
    this.status = this.querySelector('[data-merken-status]');
    this.empty = this.querySelector('[data-merken-empty]');
    this.emptyQuery = this.querySelector('[data-merken-empty-query]');
    this.input = this.querySelector('[data-merken-search]');
    this.searchWrap = this.querySelector('[data-merken-searchwrap]');
    this.clearBtn = this.querySelector('[data-merken-clear]');

    // Zoeksleutel eenmalig voorberekenen: diakrieten weg, lowercase.
    this.keys = new WeakMap();
    this.tiles.forEach((tile) => {
      this.keys.set(tile, MerkenIndex.normalize(tile.dataset.merkenName || tile.textContent));
    });

    this.linksByLetter = new Map();
    this.links.forEach((link) => this.linksByLetter.set(link.dataset.merkenLetter, link));

    if (this.searchWrap) {
      this.searchWrap.hidden = false;
      this.onInput = this.onInput.bind(this);
      this.input.addEventListener('input', this.onInput);
    }

    if (this.clearBtn) {
      this.onClear = this.onClear.bind(this);
      this.clearBtn.addEventListener('click', this.onClear);
    }

    this.trackHeaderHeight();
    this.observeLetters();
  }

  disconnectedCallback() {
    if (this.input && this.onInput) this.input.removeEventListener('input', this.onInput);
    if (this.clearBtn && this.onClear) this.clearBtn.removeEventListener('click', this.onClear);
    if (this.observer) this.observer.disconnect();
    if (this.headerObserver) this.headerObserver.disconnect();
  }

  // De sticky header en de A-Z balk veranderen van hoogte (promobalk, mobiel
  // wordt twee rijen), dus de offset van de balk en de scroll-margin van de
  // letterkoppen worden gemeten in plaats van gegokt. De CSS-waarden blijven
  // de fallback als er geen JS of geen ResizeObserver is.
  trackHeaderHeight() {
    const header = document.getElementById('shopify-section-header');
    const bar = this.querySelector('.merken-bar');

    const apply = () => {
      if (header) this.style.setProperty('--merken-header', `${header.offsetHeight}px`);
      // Aparte variabele, niet --merken-bar: die voedt de min-height van de
      // balk, dus terugschrijven zou een ResizeObserver-lus opleveren.
      if (bar) this.style.setProperty('--merken-baroff', `${bar.offsetHeight}px`);
    };

    apply();

    if (!('ResizeObserver' in window)) return;
    this.headerObserver = new ResizeObserver(apply);
    if (header) this.headerObserver.observe(header);
    if (bar) this.headerObserver.observe(bar);
  }

  static normalize(value) {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  onInput() {
    this.filter(MerkenIndex.normalize(this.input.value));
  }

  onClear() {
    this.input.value = '';
    this.filter('');
    this.input.focus();
  }

  filter(query) {
    let matches = 0;

    this.groups.forEach((group) => {
      let visibleInGroup = 0;

      group.querySelectorAll('[data-merken-name]').forEach((tile) => {
        const hit = !query || this.keys.get(tile).includes(query);
        tile.hidden = !hit;
        if (hit) visibleInGroup += 1;
      });

      group.hidden = visibleInGroup === 0;
      matches += visibleInGroup;

      const link = this.linksByLetter.get(group.dataset.merkenLetter);
      if (link) link.hidden = visibleInGroup === 0;
    });

    if (this.status) {
      this.status.textContent = query
        ? `${matches} van ${this.tiles.length} merken`
        : '';
    }

    if (this.empty) {
      this.empty.hidden = matches > 0;
      if (this.emptyQuery) {
        this.emptyQuery.textContent = query ? ` voor "${this.input.value.trim()}"` : '';
      }
    }
  }

  // Markeert de letter die op dit moment in beeld is, zodat je in een lange
  // lijst ziet waar je bent. Geen scroll-listener: IntersectionObserver kijkt
  // naar de strook net onder de sticky balk.
  observeLetters() {
    if (!this.links.length || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const link = this.linksByLetter.get(entry.target.dataset.merkenLetter);
          if (!link) return;
          if (entry.isIntersecting) {
            this.links.forEach((other) => other.removeAttribute('aria-current'));
            link.setAttribute('aria-current', 'true');
          }
        });
      },
      { rootMargin: '-30% 0px -65% 0px', threshold: 0 }
    );

    this.groups.forEach((group) => this.observer.observe(group));
  }
}

if (!customElements.get('merken-index')) {
  customElements.define('merken-index', MerkenIndex);
}
